import { message, Modal, notification } from 'antd';
import { produce } from 'immer';
import {
  checkServiceArea,
  fetchCityBoundary,
  fetchCloseReasonEnum,
  fetchCloseReasonEnumByCategory,
  fetchGetGuideList,
  fetchGetGuideRoads,
  fetchGetSubwayEntry,
  fetchQualificationStructure,
  fetchRelatedShops,
  fetchShopRelations,
  preCheckPoiInfo,
  searchPoiById,
  searchPoiByName,
  searchWeddingShop,
  submitAddGroups,
  submitGuide,
  submitPoiInfo,
  submitRelationsMerges,
  submitRelationsSplits,
  submitRelationsUpdate,
  submitRemoveGroup,
  submitServiceReport,
  submitWeddingInfo,
} from '@/services/api';
import { RULE_CODE, SUBMIT_ERROR_CODE_LIST } from '@/configs/basicInfo';
import cloneDeep from 'lodash/cloneDeep';
import router from 'umi/router';
import { getShopId } from '@/utils/logicUtils';
import {
  DEFAULT_GROUPS_INFO,
  DEFAULT_GUIDE_INFO,
  DEFAULT_RELATION_INFO,
  DEFAULT_SHOP_RELATION_INFO,
  DEFAULT_SHOP_STATUS,
  DEFAULT_WEDDING_INFO,
} from '@/utils/initState';
import {
  fetchFakeShopConfigs,
  fetchServiceFormValidate,
  fetchTrialTime,
  fetchTrialTimeRange,
  submitTrialTime,
} from '@/services/api';
import moment from 'moment';
import { mcEvent } from '@/utils/lxEventUtil';
import { MaybeSameShopOptions, OnlySourceOptions } from '@/configs/quickReport';
import { getSceneValue } from '@/utils/sceneInfo';
import isEqual from 'lodash/isEqual';

const baseInfoErrorHandler = async ({ code, msg = '', data = {} }) => {
  if (SUBMIT_ERROR_CODE_LIST.includes(code)) {
    // 下次提交携带本次提交的返回值，该返回值 指示 后台 跳过 AI 审核
    const info = msg.split('\n');
    Modal.error({
      title: '需要补充信息',
      content: (
        <div>
          {info.map((item, index) => {
            return (
              <div
                key={index}
                style={{
                  fontWeight: index === info.length - 2 ? 'bolder' : 'normal',
                  lineHeight: 2,
                }}
              >
                {item}
              </div>
            );
          })}
        </div>
      ),
    });

    return { code, data };
  } else {
    notification.error({
      message: '提交失败',
      description: msg,
      duration: 0,
    });
  }
};

const relationErrorHandler = res => {
  const { code, msg } = res;
  if (!code || code < 1000) {
    notification.error({
      message: '提交失败',
      description: msg || '提交失败',
      duration: 0,
    });
    return;
  } else {
    Modal.error({
      title: '报错反馈',
      content: msg || '提交失败',
      okText: '关闭',
    });
  }
};
const commonErrorHandler = async ({ code, msg = '', data = {} }) => {
  notification.error({
    message: '提交失败',
    description: msg,
    duration: 0,
  });
};

// https://km.sankuai.com/page/421018180
const basicReportError = function*({ code, msg = '', data = {}, payload = {} }, put) {
  const aiResult = (data && data.aiResult) || {};
  const dupShopInfo = data?.dupShopInfo || data?.dupShop || {};
  if (code === 705) {
    // 新旧店前置AI弹框引导提示
    if (aiResult.ruleCode && RULE_CODE.indexOf(aiResult.ruleCode) > -1) {
      yield put({
        type: 'setResultInfo',
        payload: {
          modalInfo: {
            resultData: aiResult,
          },
        },
      });
    } else {
      return yield new Promise(resolve => {
        Modal.confirm({
          content: aiResult.message || msg,
          okText: '继续提交',
          cancelText: '返回修改',
          onOk() {
            payload.skipPreAICheck = true;
            resolve({ needForceSubmit: true, payload });
          },
          onCancel() {
            resolve();
          },
        });
      });
    }
  } else if (code === 707) {
    // 707重复报错
    if (aiResult.ruleCode === 'r300003145') {
      yield put({
        type: 'setResultInfo',
        payload: {
          showBrandQualification: true,
        },
      });
    } else {
      Modal.confirm({
        title: '报错反馈',
        content: aiResult.message || msg,
        okText: '查看详情',
        onOk() {
          window.location.href = '/manage/pc/#/info/my-report';
        },
        onCancel() {},
      });
    }
  } else if (code === 709) {
    Modal.info({
      title: '资质要求',
      content: aiResult.message || msg,
      okText: '返回上传',
      onOk() {},
    });
  } else if (code === 711) {
    // 返回修改或继续提交_返回资质
    return yield new Promise(resolve => {
      Modal.confirm({
        title: '资质要求',
        content: aiResult.message || msg,
        okText: '返回修改',
        cancelText: '继续上传',
        onOk() {
          const proofDom = document.querySelector('.structure-proof');
          if (proofDom) {
            proofDom.scrollIntoView();
          }
          resolve();
        },
        onCancel() {
          payload.skipPreAICheck = true;
          resolve({ needForceSubmit: true, payload });
        },
      });
    });
  } else if (code === 726) {
    // 726重复报错
    yield put({
      type: 'setResultInfo',
      payload: {
        dupShopInfo,
      },
    });
  } else if (code === 728) {
    // 单按钮弹窗 一个按钮，点击后继续提交
    return yield new Promise(resolve => {
      Modal.error({
        title: '提示',
        content: aiResult.message || msg,
        okText: aiResult.buttonTxtList[0],
        onOk() {
          payload.skipPreAICheck = true;
          resolve({ needForceSubmit: true, payload });
        },
      });
    });
  } else if (code === 900) {
    // 报停业强提醒
    yield put({
      type: 'setResultInfo',
      payload: {
        showCloseAlert: true,
      },
    });
    LXAnalytics('moduleView', 'b_pdc_0lxg8e19_mv', {
      type: 2,
    });
  } else {
    if (code !== 703) {
      // 703不用弹框
      Modal.error({
        title: '提示',
        content: aiResult.message || msg,
        okText: '关闭',
        onOk() {},
        onCancel() {},
      });
    }
  }
  // 如果提交失败，errorHandler显示modal，同时更新返回数据，页面显示红色校验提示等逻辑
  yield put({
    type: 'setResultInfo',
    payload: {
      isHaveSubmit: true, //此字段用于form表单的提交时候进行校验显示
      aiResult,
    },
  });
};

const errHandler = async res => {
  message.error(res.msg || '系统异常，请稍后重试');
  return res;
};

const serviceReportError = async ({ code, msg = '', data = {} }) => {
  switch (+code) {
    case 404:
    case 400:
    case 410:
    case 500:
      message.error(msg || '系统异常，请稍后重试');
      break;
    case 704:
      const aiResult = data?.aiResult;
      Modal.error({
        title: '提示',
        content: aiResult?.message || '系统异常，请稍后重试',
        okText: '关闭',
      });
      break;
    case 703:
      return { code, data };
    default:
      message.error(msg || '系统异常，请稍后重试');
      return { code, data };
  }
};

export default {
  namespace: 'poi',

  state: {
    searchFilter: {
      type: 'dpId', // 按何种方式搜索 dpId mtId dealId name. 默认按照点评门店
      key: '', // 用户在搜索框的输入
      // key: '4078432', // 用户在搜索框的输入
      platform: 'dp', // 按门店名称时，对应平台 dp mt
      provinceId: -1, // -1 代表默认全部
      cityId: -1,
      districtId: -1,
    },
    searchPoiResult: {},
    searchPoiList: [],
    editPoiInfo: {},
    contrastPoiInfo: {}, // 用于在基本信息报错中，存储通过 id 请求来的数据，进而在点击提交时对比数据是否被更改，注意不要修改此对象
    searchByNameCurrentPage: 0, // 按名称搜索门店 当前页码
    searchByNameTotalItems: 0, // 按名称搜索门店 总数据量

    // 门店状态相关数据存储在此
    shopStatusInfo: cloneDeep(DEFAULT_SHOP_STATUS),

    // 路引信息相关数据存储在此
    guideInfo: cloneDeep(DEFAULT_GUIDE_INFO),

    // 婚宴副本相关数据存储在此
    weddingInfo: cloneDeep(DEFAULT_WEDDING_INFO),

    // 门店重复相关数据存储在此
    relationInfo: cloneDeep(DEFAULT_RELATION_INFO),

    // 关联关系报错相关数据存储在此
    shopRelationInfo: cloneDeep(DEFAULT_SHOP_RELATION_INFO),

    // 连锁相关数据存储在此
    groupsInfo: cloneDeep(DEFAULT_GROUPS_INFO),

    //其他poi数据
    otherPoiInfo: {
      mutxCateIds: [], //与主类目互斥的类目id集合
      showGrayPoiInfo: false, // 是否在基本信息报错展示灰度转正
      showBusinessSpecificPoiInfo: false, // 是否在基本信息报错展示新企转正
      showHighRiskPoiInfo: false, // 是否在基本信息报错展示高危门店转正
      isHighRiskPoi: false, // 是否为高危门店（useType === 15）
    },

    // 接口返回的result
    resultInfo: {
      modalInfo: null,
      // 726重复报错相关信息
      dupShopInfo: null,
      //基本信息报错，和营业状态报错目前有使用返回的字段
      aiResult: {
        fieldMap: {},
      },
      showBrandQualification: true,
    },
    cityBoundary: [], // 服务范围城市边界
    validServiceArea: false, // 超出城市范围
    normalBusinessHoursRequired: true, // 正常营业时间是否必须
    // 资质结构化相关
    qualificationStructure: [],
    qualificationLoading: 0,
    qualificationUserMsg: '',
  },

  effects: {
    // 搜索门店相关
    *searchPoiById({ payload }, { call, put }) {
      // 前置清空
      yield put({
        type: 'clearResultInfo',
      });

      const { shopId, source, type } = payload;
      const response = yield call(searchPoiById, { shopId, source });
      // 将 用户输入的搜索关键词，放到 searchFilter 的 key 字段
      yield put({
        type: 'setSearchFilter',
        payload: {
          key: shopId,
          type,
        },
      });
      yield put({
        type: 'setSearchPoiResultPagesInfo',
        payload: {
          currentPage: 1,
          totalItems: 1,
        },
      });

      // 将当前结果保存在 contrastPoiInfo 中，
      yield put({
        type: 'setContrastPoiInfo',
        payload: response,
      });
      yield put({
        type: 'setSearchPoiResultAndSetEditState',
        payload: response,
      });
    },
    *searchPoiByName({ payload }, { call, put }) {
      const { currentPage = 1, data = [], totalItems = 0 } =
        (yield call(searchPoiByName, payload)) || {};
      // 将 用户输入的搜索关键词，放到 searchFilter 的 key 字段
      yield put({
        type: 'setSearchFilter',
        payload: {
          key: payload.shopName,
        },
      });
      yield put({
        type: 'setSearchPoiResultPagesInfo',
        payload: {
          currentPage,
          totalItems,
        },
      });
      yield put({
        type: 'setSearchPoiResultList',
        payload: data ? data : [],
      });
    },

    *preCheckBaseInfoErrorInfo({ payload }, { call, put }) {
      const { code, data, msg } = yield call(preCheckPoiInfo, payload);

      // 正常情况下 data 为流程 id 字符串
      if (code === 200) {
        return { isSuccess: true, msg };
      } else {
        const response = yield basicReportError({ code, data, msg, payload }, put);
        if (response?.needForceSubmit) {
          return yield put({
            type: 'preCheckBaseInfoErrorInfo',
            payload: { ...payload, skipPreAICheck: true },
          });
        }
        return { isSuccess: false };
      }
    },
    // 门店基础信息报错相关
    *submitBaseInfoErrorInfo({ payload }, { call, put, select }) {
      const callback = function*() {
        const { notRedirect = false, ...restArgs } = payload;
        // 直接使用 restArgs，路径替换会由 request.js 中的逻辑自动处理
        const { code, data, msg } = yield call(submitPoiInfo, restArgs);

        // 正常情况下 data 为流程 id 字符串
        if (code === 200) {
          if (notRedirect) return { isSuccess: true, msg };

          const { editPoiInfo } = yield select(state => state.poi);
          yield put({
            type: 'clearEditPoiInfo',
          });
          yield put({
            type: 'clearResultInfo',
          });
          yield put({
            type: 'success/setSuccessField',
            payload: {
              flowId: data?.flowIds,
              successTextHtml: msg,
              referer: 'base',
              shopId: editPoiInfo.dpShopId,
            },
          });
          router.push('/errors/result/success');
        } else {
          const response = yield basicReportError({ code, data, msg, payload }, put);
          if (response?.needForceSubmit) {
            yield callback();
          }
        }
      };
      yield callback();
    },
    // 获取资质结构的逻辑 门店报错和基本信息报错请求的同一个接口
    *fetchQualificationStructure({ payload }, { call, select, put }) {
      const qualificationStructure = yield select(state => state.poi.qualificationStructure);
      // 资质加载中
      yield put({
        type: 'updateFields',
        payload: {
          qualificationLoading: 1,
        },
      });
      // 营业时间从json变成string
      if (payload?.businessHoursObj) {
        payload.businessHours = JSON.stringify(payload.businessHoursObj);
        delete payload.businessHoursObj;
      }
      const data = yield call(
        fetchQualificationStructure,
        payload,
        'shopReport',
        ({ code, msg = '', data = '' }) => {
          console.error('资质查询失败', code, msg, data);
        }
      );
      // 比较前后资质是否发生变更，未变更则不更新
      const isChanged = !isEqual(qualificationStructure || {}, data?.qualificationStructures || {});
      console.log('资质结果变更=', isChanged);
      yield put({
        type: 'updateFields',
        payload: {
          ...(isChanged ? { qualificationStructure: data?.qualificationStructures || [] } : {}),
          qualificationLoading: 0,
          qualificationUserMsg: data?.userMsg || '',
        },
      });
    },
    // 用于基本信息和营业状态报错表单的更新
    *updateEditPoiInfo({ payload }, { put }) {
      yield put({
        type: 'setEditPoiInfo',
        payload,
      });
    },

    *initCloseReasonEnum({ payload }, { call, put, select }) {
      const { closeReason } = yield call(fetchCloseReasonEnum);
      yield put({
        type: 'setChangeShopStatus',
        payload: { closeReasonEnum: closeReason },
      });
    },

    // 非境外酒店类目的停业原因获取
    *initHotelCloseReasonEnum({ payload }, { call, put, select }) {
      const { closeReason } = yield call(fetchCloseReasonEnumByCategory, payload);
      yield put({
        type: 'setChangeShopStatus',
        payload: { closeReasonEnum: closeReason },
      });
    },
    *fetchTrialTime({ payload }, { call, put, select }) {
      const { dpShopId } = yield select(state => state.poi.searchPoiResult);
      const data = yield call(fetchTrialTime, {
        dpShopId,
      });
      if (!data) return;
      const { trialOpenDay, openDay, trialOpenType, fuzzyOpenTime } = data;
      yield put({
        type: 'setChangeShopStatus',
        payload: { trialOpenDay, openDay, trialOpenType: trialOpenType || 0, fuzzyOpenTime },
      });
    },
    *fetchTrialTimeRange({ payload }, { call, put, select }) {
      const { dpShopId } = yield select(state => state.poi.editPoiInfo);
      const { trialMinDate, trailMaxDate, openMinDate, openMaxDate } = yield call(
        fetchTrialTimeRange,
        { dpShopId, ...payload }
      );
      yield put({
        type: 'setChangeShopStatus',
        payload: {
          trialMinDate,
          trailMaxDate,
          openMinDate,
          openMaxDate,
        },
      });
    },
    *submitTrialTime({ payload }, { call, select, put }) {
      const { dpShopId } = yield select(state => state.poi.editPoiInfo);
      const { code, data, msg } = yield call(submitTrialTime, { dpShopId, ...payload }, e =>
        console.warn(e)
      );
      if (code === 200) {
        yield put({
          type: 'setChangeShopStatus',
          payload: {
            openDay: payload.openDay ? moment(payload.openDay).valueOf() : null,
            trialOpenDay: payload.trialOpenDay ? moment(payload.trialOpenDay).valueOf() : null,
            softOpenModalVisible: false,
          },
        });

        Modal.success({
          content: '提交成功，感谢反馈',
        });
      } else {
        Modal.error({
          title: '提交失败',
          content: msg || data || '请稍后重试',
        });
      }
    },
    //商店状态报错相关
    *preCheckShopStatusError({ payload }, { call, put, select }) {
      // 直接使用 payload，路径替换会由 request.js 中的逻辑自动处理
      const { code, data, msg } = yield call(preCheckPoiInfo, payload);
      // 正常情况下 data 为流程 id 字符串
      if (code === 200) {
        return { isSuccess: true, msg };
      } else {
        const response = yield basicReportError({ code, data, msg, payload }, put);
        if (response?.needForceSubmit) {
          return yield put({
            type: 'preCheckShopStatusError',
            payload: { ...payload, skipPreAICheck: true },
          });
        }
        return { isSuccess: false };
      }
    },
    *submitShopStatusError({ payload }, { call, put, select }) {
      // 直接使用 payload，路径替换会由 request.js 中的逻辑自动处理
      const { code, data, msg } = yield call(submitPoiInfo, payload);
      //正常情况下 data 为流程 id 字符串
      if (code === 200 && data.flowIds) {
        const { editPoiInfo } = yield select(state => state.poi);
        yield put({
          type: 'clearResultInfo',
        });
        yield put({
          type: 'success/setSuccessField',
          payload: {
            flowId: data.flowIds,
            successTextHtml: msg,
            referer: 'power',
            shopId: editPoiInfo.dpShopId,
          },
        });

        router.push('/errors/result/success');
      } else {
        const response = yield basicReportError({ code, data, msg, payload }, put);
        if (response?.needForceSubmit) {
          yield put({
            type: 'submitShopStatusError',
            payload,
          });
        }
      }
    },

    *checkNormalBizHoursRequired({ payload }, { call, put, select }) {
      const searchPoiResult = yield select(state => state.poi.searchPoiResult);
      const data = yield call(fetchFakeShopConfigs, {
        ...payload,
        shopId: searchPoiResult.dpShopId,
        source: 'dp',
      });
      if (data) {
        yield put({
          type: 'setNormalBizHoursRequired',
          payload: {
            normalBusinessHoursRequired: data.normalBusinessHoursRequired,
          },
        });
      }
    },

    *fetchFakeShopConfigs({ payload }, { call, put, select }) {
      const searchPoiResult = yield select(state => state.poi.searchPoiResult);
      if (searchPoiResult) {
        const data = yield call(fetchFakeShopConfigs, {
          shopId: searchPoiResult.dpShopId,
          source: 'dp',
        });
        if (data && data.reportType) {
          yield put({
            type: 'setChangeShopStatus',
            payload: {
              reportType: data.reportType,
            },
          });
        } else {
          yield put({
            type: 'setChangeShopStatus',
            payload: {
              reportType: null,
            },
          });
        }
      }
    },

    *submitRelationsUpdate({ payload }, { call, put, select }) {
      mcEvent('b_pdc_d5n8igwj_mc');
      const { code, data, msg } = yield call(submitRelationsUpdate, payload);
      if (code === 200 && data.flowIds) {
        yield put({
          type: 'success/setSuccessField',
          payload: {
            flowId: data.flowIds,
            successTextHtml: data.message || msg,
          },
        });

        router.push('/errors/result/success');
      } else {
        const response = yield basicReportError({ code, data, msg, payload }, put);
        if (response?.needForceSubmit) {
          yield put({
            type: 'submitRelationsUpdate',
            payload,
          });
        }
      }
    },

    *submitOnlySourceInfo({ payload }, { call, put, select }) {
      const searchPoiResult = yield select(state => state.poi.searchPoiResult);
      const { from, ...submitParams } = payload;
      const res = yield call(submitPoiInfo, {
        ...submitParams,
        shopId: searchPoiResult.dpShopId,
        source: 'dp',
      });
      if (!res) {
        message.error('请求失败，请稍后再试');
        return;
      }
      const { code, data, msg } = res || {};
      //正常情况下 data 为流程 id 字符串
      if (code === 200 && data.flowIds) {
        const options = from === 'MaybeSameShop' ? MaybeSameShopOptions : OnlySourceOptions;
        const label = options.find(i => i.value === payload.useType)?.label;
        yield put({
          type: 'success/setSuccessField',
          payload: {
            flowId: data.flowIds,
            successTextHtml: `<span style="color: #0A0A0A">感谢反馈，您已选择“${label}”，门店状态审核中，审核通过后，重新进入报错页可对门店信息报错</span>`,
          },
        });

        router.push('/errors/result/success');
      } else {
        const response = yield basicReportError({ code, data, msg, payload }, put);
        if (response?.needForceSubmit) {
          yield put({
            type: 'submitOnlySourceInfo',
            payload,
          });
        }
      }
    },

    // 虚假门店相关报错
    *submitFakeInfo({ payload }, { call, put }) {
      const { code, data, msg } = yield call(submitPoiInfo, payload);
      //正常情况下 data 为流程 id 字符串
      if (code === 200 && data.flowIds) {
        yield put({
          type: 'success/setSuccessField',
          payload: {
            flowId: data.flowIds,
            successTextHtml: msg,
          },
        });

        router.push('/errors/result/success');
      } else {
        const response = yield basicReportError({ code, data, msg, payload }, put);
        if (response?.needForceSubmit) {
          yield put({
            type: 'submitFakeInfo',
            payload,
          });
        }
      }
    },

    // 路引信息相关
    *searchGuideList({ payload }, { call, put }) {
      const errorHandler = commonErrorHandler;
      const guideList = yield call(fetchGetGuideList, payload, errorHandler);
      yield put({
        type: 'setGuideInfo',
        payload: {
          guideList: guideList || [],
        },
      });
    },
    *searchGuideRoads({ payload }, { call, put }) {
      const errorHandler = commonErrorHandler;
      const data = yield call(fetchGetGuideRoads, payload, errorHandler);
      yield put({
        type: 'setGuideInfo',
        payload: {
          guideRoads: data?.guideInfo || '',
        },
      });
    },
    *searchSubwayEntry({ payload }, { call, put }) {
      const errorHandler = commonErrorHandler;
      const guideSubwayEntry = yield call(fetchGetSubwayEntry, payload, errorHandler);
      yield put({
        type: 'setGuideInfo',
        payload: {
          guideSubwayEntry: guideSubwayEntry || {},
        },
      });
    },
    *submitGuide({ payload }, { call, put }) {
      const { code, data, msg } = yield call(submitGuide, payload);
      if (code === 200 && data.flowIds) {
        yield put({
          type: 'success/setSuccessField',
          payload: {
            flowId: data.flowIds,
            successTextHtml: msg,
          },
        });
        router.push('/errors/result/success');
      } else {
        yield commonErrorHandler({ code, data, msg });
      }
    },
    // 婚宴副本报错相关
    *submitWeddingInfo({ payload }, { call, put }) {
      const response = yield call(submitWeddingInfo, payload);
      if (response?.code === 200) {
        router.push('/errors/result/success');
      } else {
        yield commonErrorHandler(response);
      }
    },
    *searchWeddingShop({ payload }, { call, put, select }) {
      const { code, data } = yield call(searchWeddingShop, payload);
      if (code === 200) {
        yield put({
          type: 'setRelationInfo',
          payload: {
            mergeShopList: data || [],
          },
        });
      }
    },

    // 关联关系报错
    *fetchRelatedShops({ payload }, { call, put }) {
      const { yellowTips, shops } = yield call(fetchRelatedShops, payload, errHandler);
      yield put({
        type: 'setRelatedInfo',
        payload: {
          shopRelationList: shops,
          yellowTips,
        },
      });
    },

    // 门店重复相关报错
    *fetchShopRelations({ payload }, { call, put }) {
      const shopInfoList = yield call(fetchShopRelations, payload);
      yield put({
        type: 'setRelationInfo',
        payload: {
          shopInfoList,
          source: 'dp',
        },
      });
    },
    *submitRelations({ payload }, { call, put, select }) {
      const {
        type,
        addInfo: { pics = [], comment = '' } = {},
        notRedirect = false,
        reportType,
      } = payload;

      const { editPoiInfo, relationInfo } = yield select(state => state.poi);
      const dpShopId = editPoiInfo?.dpShopId;
      let res = {};
      let dpIds = [];
      switch (type) {
        case '/errors/repeat/merge':
          const { mergeShopList = [] } = relationInfo || {};
          dpIds = mergeShopList.map(item => item.dpShopId);
          res = yield call(submitRelationsMerges, {
            shopId: dpShopId,
            repeatShopIds: dpIds,
            reportType,
            picInfos: pics,
            comment,
          });
          break;
        case '/errors/repeat/split':
          const {
            shopInfoList = [],
            splitShopInfo: { dpShopId: dpShopIdMerge },
          } = relationInfo || {};
          const mainShopId = shopInfoList[0].dpShopId;
          res = yield call(submitRelationsSplits, {
            mainShopId,
            releaseShopId: dpShopIdMerge,
            picInfos: pics,
            comment,
          });
          break;
        default:
          // from 阿波罗拜访
          dpIds = payload.mergeShopList.map(item => item.dpShopId);
          res = yield call(submitRelationsMerges, {
            shopId: payload.shopId,
            repeatShopIds: dpIds,
            picInfos: payload.picInfos || [],
            sceneInfo: payload.sceneInfo,
            ext: payload.ext,
          });
          break;
      }
      if (res.code === 200 && res.data) {
        if (notRedirect) return { isSuccess: true, msg: res.msg };
        yield put({
          type: 'clearEditPoiInfo',
        });
        yield put({
          type: 'success/setSuccessField',
          payload: {
            flowId: res.data.flowIds,
            successTextHtml: res.msg,
          },
        });
        router.push('/errors/result/success');
      } else {
        relationErrorHandler(res);
      }
    },
    *searchPoiByIdInRelations({ payload }, { call, put }) {
      const response = yield call(searchPoiById, payload);
      yield put({
        type: 'setRelationInfo',
        payload: {
          shopList: response ? [response] : [],
          shopListPaginationInfo: {
            currentPage: 0,
            totalItems: 0,
          },
        },
      });
    },
    *searchPoiByNameInRelations({ payload }, { call, put }) {
      const response = (yield call(searchPoiByName, payload)) || {};
      const { currentPage = 1, totalItems = 0, data = [] } = response;
      yield put({
        type: 'setRelationInfo',
        payload: {
          shopListPaginationInfo: {
            currentPage,
            totalItems,
          },
          shopList: data,
        },
      });
    },
    // 解除连锁
    *submitRemoveGroup({ payload }, { call, put }) {
      const { code, data, msg } = yield call(submitRemoveGroup, payload);

      if (code === 200) {
        yield put({
          type: 'clearEditPoiInfo',
        });
        yield put({
          type: 'success/setSuccessField',
          payload: {
            flowId: data,
            successTextHtml: msg,
          },
        });
        router.push('/errors/result/success');
      } else {
        notification.error({
          message: '提交失败',
          description: msg,
          duration: 0,
        });
      }
    },
    *submitAddGroups({ payload }, { call, put }) {
      const { code, data, msg } = yield call(submitAddGroups, payload);
      if (code === 200) {
        yield put({
          type: 'clearEditPoiInfo',
        });
        yield put({
          type: 'setGroupsInfo',
          payload: {
            phoneList: '',
            businessHoursObj: null,
            changeStatus: 5,
            mergeGroupsList: [], // 将要被关联的门店列表
            picUrls: null,
            comment: '',
          },
        });
        yield put({
          type: 'success/setSuccessField',
          payload: {
            flowId: data,
            successTextHtml: msg,
          },
        });
        router.push('/errors/result/success');
      } else {
        return baseInfoErrorHandler({ code, msg, data });
      }
    },

    *checkServiceArea({ payload }, { call, put }) {
      // 检查绘制范围是否超出边界
      const response = yield call(checkServiceArea, payload, errHandler);
      return response;
    },

    *fetchCityBoundary({ payload }, { call, put }) {
      const response = yield call(fetchCityBoundary, payload);
      if (response) {
        if (response.length) {
          yield put({
            type: 'setCityBoundary',
            payload: response,
          });
        } else {
          message.error('查询城市边界失败');
        }
      }
    },
    *validateServiceReport({ payload }, { call, put }) {
      yield put({
        type: 'clearResultInfo',
      });
      const res = yield call(fetchServiceFormValidate, payload);
      const { code, data } = res || {};
      if (code === 703) {
        // 如果提交失败，errorHandler显示modal，同时更新返回数据，页面显示红色校验提示等逻辑
        yield put({
          type: 'setResultInfo',
          payload: {
            aiResult: (data || {}).aiResult || {},
          },
        });
      }
    },
    *submitServiceReport({ payload }, { call, put }) {
      const { code, msg, data } = yield call(submitServiceReport, payload);
      // 正常情况下 data 为流程 id 字符串，后续添加了 LE 业务字段提报
      if (code === 200) {
        yield put({
          type: 'clearResultInfo',
        });
        yield put({
          type: 'success/setSuccessField',
          payload: {
            flowId: data || '',
            successTextHtml: msg,
          },
        });
        router.push('/errors/result/success');
      } else {
        serviceReportError({ code, msg, data });
        // 如果提交失败，errorHandler显示modal，同时更新返回数据，页面显示红色校验提示等逻辑
        yield put({
          type: 'setResultInfo',
          payload: {
            aiResult: (data || {}).aiResult || {},
          },
        });
      }
    },
  },

  reducers: {
    updateFields(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
    // 校验是否超出边界
    setCheckServiceArea(state, action) {
      return {
        ...state,
        validServiceArea: action.payload,
      };
    },
    // 城市边界
    setCityBoundary(state, action) {
      return {
        ...state,
        cityBoundary: action.payload,
      };
    },
    // 搜索门店相关
    setSearchFilter(state, action) {
      const { payload } = action;
      const { searchFilter } = state;
      return {
        ...state,
        searchFilter: {
          ...searchFilter,
          ...payload,
        },
      };
    },
    setContrastPoiInfo(state, action) {
      return {
        ...state,
        contrastPoiInfo: {
          ...action.payload,
        },
      };
    },
    setCloseReasonEnum(state, { payload }) {
      return {
        ...state,
        closeReasonEnum: payload.closeReason,
      };
    },
    // 初始化配置灰度/低质转正
    setSearchPoiResultAndSetEditState(state, action) {
      const searchPoiResult = action.payload;
      const otherPoiInfo = {};
      if (searchPoiResult) {
        const { shopId } = getShopId(searchPoiResult);
        if (!shopId) {
          message.error('门店状态异常(缺少必要的 dpId 及 mtId)，请联系开发人员检查');
        }
      }
      return {
        ...state,
        searchPoiResult,
        editPoiInfo: searchPoiResult && cloneDeep(searchPoiResult), // 只有一个结果，直接置于选中状态 // 当前版本，直接赋值会传递引用
        otherPoiInfo,
      };
    },
    setSearchPoiResultPagesInfo(state, action) {
      // 设置翻页信息
      const { currentPage, totalItems } = action.payload;
      return {
        ...state,
        searchByNameCurrentPage: currentPage,
        searchByNameTotalItems: totalItems,
      };
    },
    setSearchPoiResultList(state, action) {
      const searchPoiList = action.payload;
      return {
        ...state,
        searchPoiResult: {}, // 清空按 id 查询的结果
        searchPoiList,
      };
    },
    setEditPoiInfo(state, action) {
      return {
        ...state,
        editPoiInfo: {
          ...state.editPoiInfo,
          ...action.payload,
          bizAttribute: {
            ...state.editPoiInfo?.bizAttribute,
            ...action.payload?.bizAttribute,
          },
        },
      };
    },
    resetEditPoiInfo(state) {
      return {
        ...state,
        editPoiInfo: cloneDeep(state.searchPoiResult || {}),
      };
    },
    clearEditPoiInfo(state) {
      return {
        ...state,
        editPoiInfo: {},
      };
    },

    // 商店状态报错相关
    initShopStatusInfo(state, action) {
      return {
        ...state,
        editPoiInfo: {
          ...state.editPoiInfo,
          ...action.payload,
        },
        shopStatusInfo: cloneDeep(DEFAULT_SHOP_STATUS),
      };
    },
    setChangeShopStatus(state, action) {
      const { shopStatusInfo } = state;
      return {
        ...state,
        shopStatusInfo: {
          ...shopStatusInfo,
          ...action.payload,
        },
      };
    },
    clearShopStatusInfo(state) {
      return {
        ...state,
        shopStatusInfo: cloneDeep(DEFAULT_SHOP_STATUS),
      };
    },

    // 路引信息相关
    setGuideInfo(state, action) {
      return {
        ...state,
        guideInfo: {
          ...state.guideInfo,
          ...action.payload,
        },
      };
    },
    clearGuideInfo(state, action) {
      return {
        ...state,
        guideInfo: cloneDeep(DEFAULT_GUIDE_INFO),
      };
    },

    // 婚宴副本报错相关
    setChangeWeddingInfo(state, action) {
      const { payload } = action;
      const { weddingInfo } = state;
      return {
        ...state,
        weddingInfo: {
          ...weddingInfo,
          ...payload,
        },
      };
    },
    clearWeddingInfo(state) {
      return {
        ...state,
        weddingInfo: cloneDeep(DEFAULT_WEDDING_INFO),
      };
    },

    // 关联关系报错
    setRelatedInfo(state, action) {
      const { shopRelationInfo } = state;
      return {
        ...state,
        shopRelationInfo: {
          ...shopRelationInfo,
          ...action.payload,
        },
      };
    },

    // 门店重复相关报错
    setRelationInfo(state, action) {
      const { relationInfo } = state;
      return {
        ...state,
        relationInfo: {
          ...relationInfo,
          ...action.payload,
        },
      };
    },
    clearRelationInfo(state) {
      return {
        ...state,
        relationInfo: {
          ...cloneDeep(DEFAULT_RELATION_INFO),
          // TODO shopInfoList 不应该放在此数据结构里，这里不清除
          shopInfoList: state.relationInfo.shopInfoList,
        },
      };
    },
    clearRelationShopInfo(state) {
      return {
        ...state,
        // TODO shopInfoList 不应该放在此数据结构里
        relationInfo: cloneDeep(DEFAULT_RELATION_INFO),
      };
    },

    // 连锁相关
    setGroupsInfo(state, action) {
      const { groupsInfo } = state;
      const { payload } = action;
      return {
        ...state,
        groupsInfo: {
          ...groupsInfo,
          ...payload,
        },
      };
    },
    clearGroupsInfo(state) {
      return {
        ...state,
        groupsInfo: cloneDeep(DEFAULT_GROUPS_INFO),
      };
    },
    //设置poi数据相关的其他数据
    setOtherPoiInfo(state, action) {
      const { otherPoiInfo } = state;
      return {
        ...state,
        otherPoiInfo: {
          ...otherPoiInfo,
          ...action.payload,
        },
      };
    },
    setNormalBizHoursRequired(state, action) {
      const { payload } = action;
      return {
        ...state,
        normalBusinessHoursRequired: payload.normalBusinessHoursRequired,
      };
    },
    setResultInfo(state, action) {
      const { resultInfo } = state;
      return {
        ...state,
        resultInfo: {
          ...resultInfo,
          ...action.payload,
        },
      };
    },
    clearResultInfo(state) {
      return {
        ...state,
        resultInfo: {},
      };
    },
  },
};
